import React, { useState } from 'react';
import { ApolloProvider, gql, useQuery, useMutation } from '@apollo/client';
import client from '../lib/apolloClient';
import PartnerLeadDetail from '../components/Succeedo/PartnerWorkspace/PartnerLeadDetail';

const ACTION_FAILED = 'That did not go through. Please try again.';

const PARTNER_LEAD_QUERY = gql`
  query PartnerLeadDetail {
    currentUser {
      id
      name
      succeedo_persona
      action_plan {
        open_count(source: "succeedo")
        completed_count(source: "succeedo")
      }
    }
    partner_leads
  }
`;

const UPDATE_INTRODUCTION_REQUEST = gql`
  mutation UpdateIntroductionRequest($id: ID!, $status: String!) {
    update_introduction_request(id: $id, status: $status) {
      introduction_request
      errors
    }
  }
`;

function deriveInitials(name) {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  const first = parts[0] ? parts[0][0] : '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
}

function PartnerLeadDetailLoader({ leadId }) {
  const { data, loading, refetch } = useQuery(PARTNER_LEAD_QUERY);
  const [updateRequest] = useMutation(UPDATE_INTRODUCTION_REQUEST);
  const [actionError, setActionError] = useState(null);

  const user = data?.currentUser;
  const persona = (user?.succeedo_persona || 'seller').toUpperCase();
  const userName = user?.name || '';
  const userInitials = deriveInitials(userName);

  const actionPlan = user?.action_plan || {};
  const openCount = actionPlan.open_count || 0;
  const completedCount = actionPlan.completed_count || 0;
  const total = Math.max(openCount + completedCount, 5);
  const planProgress = {
    completed: completedCount,
    total,
    percentage: total > 0 ? Math.round((completedCount / total) * 100) : 0,
  };

  const leads = data?.partner_leads || [];
  const lead = leads.find((l) => String(l.id) === String(leadId));

  const handleAccept = async () => {
    try {
      setActionError(null);
      const { data: result } = await updateRequest({
        variables: { id: String(leadId), status: 'accepted' },
      });
      const errors = result?.update_introduction_request?.errors || [];
      if (errors.length > 0) {
        setActionError(errors.join(' '));
        return;
      }
      if (typeof window.ahoy !== 'undefined') {
        window.ahoy.track('succeedo_partner_lead_accepted', { lead_id: leadId });
      }
      refetch();
    } catch (e) {
      setActionError(ACTION_FAILED);
    }
  };

  const handleDecline = async (reason) => {
    try {
      setActionError(null);
      const { data: result } = await updateRequest({
        variables: { id: String(leadId), status: 'declined' },
      });
      const errors = result?.update_introduction_request?.errors || [];
      if (errors.length > 0) {
        setActionError(errors.join(' '));
        return;
      }
      if (typeof window.ahoy !== 'undefined') {
        window.ahoy.track('succeedo_partner_lead_declined', { lead_id: leadId, reason });
      }
      refetch();
    } catch (e) {
      setActionError(ACTION_FAILED);
    }
  };

  const handleComplete = async () => {
    try {
      setActionError(null);
      const { data: result } = await updateRequest({
        variables: { id: String(leadId), status: 'completed' },
      });
      const errors = result?.update_introduction_request?.errors || [];
      if (errors.length > 0) {
        setActionError(errors.join(' '));
        return;
      }
      if (typeof window.ahoy !== 'undefined') {
        window.ahoy.track('succeedo_partner_lead_completed', { lead_id: leadId });
      }
      refetch();
    } catch (e) {
      setActionError(ACTION_FAILED);
    }
  };

  return (
    <PartnerLeadDetail
      lead={lead || null}
      loading={loading}
      onAccept={handleAccept}
      onDecline={handleDecline}
      onComplete={handleComplete}
      persona={persona}
      userName={userName}
      userInitials={userInitials}
      planProgress={planProgress}
      actionPlanBadge={openCount}
      isPartner
      actionError={actionError}
    />
  );
}

const SucceedoPartnerLeadDetailContainer = (props, _railsContext) => (
  <ApolloProvider client={client}>
    <PartnerLeadDetailLoader leadId={props.leadId} />
  </ApolloProvider>
);

export default SucceedoPartnerLeadDetailContainer;
